import { type ChangeEvent, useEffect, useId, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { DynamicTableConfig, DynamicTableRowConfig, Question } from '../../config/types';
import {
  chainValidations,
  checkAllFieldsHaveValues,
  groupValidation,
  interpolateQuestionText,
  objectToString,
  stringToObject,
} from '../../utils/form-utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
  interpolationValues?: Record<string, unknown>;
};

export const getDynamicTableFieldName = (rowKey: string, columnKey: string) =>
  `${rowKey}${columnKey}`;

export const getDynamicTableEditableKeys = (config?: DynamicTableConfig) => {
  if (!config) return [];
  return config.rows.flatMap((row) =>
    config.columns
      .filter((column) => column.enabled !== false && !column.calculation)
      .map((column) => getDynamicTableFieldName(row.key, column.key)),
  );
};

export const getDynamicTableKeys = (config?: DynamicTableConfig) => {
  if (!config) return [];
  return config.rows.flatMap((row) =>
    config.columns.map((column) => getDynamicTableFieldName(row.key, column.key)),
  );
};

const toNumber = (value: string | undefined) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isEditableColumn = (column: DynamicTableConfig['columns'][number]) =>
  column.enabled !== false && !column.calculation;

const getEditableColumns = (config: DynamicTableConfig) => config.columns.filter(isEditableColumn);

const hasValue = (value: string | undefined) => value !== undefined && value !== '';

const isExplicitZero = (value: string | undefined) => hasValue(value) && toNumber(value) === 0;

const getRowTotal = (config: DynamicTableConfig, rowKey: string, values: Record<string, string>) =>
  getEditableColumns(config).reduce(
    (total, column) => total + toNumber(values[getDynamicTableFieldName(rowKey, column.key)]),
    0,
  );

const isRowFullyAnswered = (
  config: DynamicTableConfig,
  rowKey: string,
  values: Record<string, string>,
) =>
  getEditableColumns(config).every((column) =>
    hasValue(values[getDynamicTableFieldName(rowKey, column.key)]),
  );

const stripLeadingZeroes = (value: string, allowDecimal: boolean) => {
  const negative = value.startsWith('-');
  const unsignedValue = negative ? value.slice(1) : value;
  const [integerPart, decimalPart] = unsignedValue.split('.');
  const strippedInteger = integerPart.replace(/^0+(?=\d)/, '');
  const normalisedInteger =
    strippedInteger === '' && decimalPart !== undefined ? '0' : strippedInteger;
  const normalisedValue =
    allowDecimal && decimalPart !== undefined
      ? `${normalisedInteger}.${decimalPart}`
      : normalisedInteger;

  return `${negative ? '-' : ''}${normalisedValue}`;
};

export const normaliseDynamicTableInputValue = (
  value: string,
  row: Pick<DynamicTableRowConfig, 'allowDecimal' | 'allowNegativeNumbers'>,
) => {
  const allowDecimal = row.allowDecimal === true;
  const allowNegativeNumbers = row.allowNegativeNumbers === true;
  let nextValue = '';
  let hasDecimal = false;

  for (const char of value) {
    if (char >= '0' && char <= '9') {
      nextValue += char;
      continue;
    }
    if (char === '.' && allowDecimal && !hasDecimal) {
      nextValue += char;
      hasDecimal = true;
      continue;
    }
    if (char === '-' && allowNegativeNumbers && nextValue === '') {
      nextValue += char;
    }
  }

  return stripLeadingZeroes(nextValue, allowDecimal);
};

export const applyDynamicTableCalculations = (
  config: DynamicTableConfig,
  values: Record<string, string>,
) => {
  const nextValues = { ...values };
  const editableColumns = getEditableColumns(config);

  config.rows.forEach((row) => {
    config.columns.forEach((column) => {
      if (column.calculation !== 'rowSum') return;

      const rowHasAnyValue = editableColumns.some((sourceColumn) =>
        hasValue(nextValues[getDynamicTableFieldName(row.key, sourceColumn.key)]),
      );
      const total = getRowTotal(config, row.key, nextValues);

      nextValues[getDynamicTableFieldName(row.key, column.key)] = rowHasAnyValue
        ? String(total)
        : '';
    });
  });

  return nextValues;
};

export const applyDynamicTableConditions = (
  config: DynamicTableConfig,
  values: Record<string, string>,
) => {
  const nextValues = applyDynamicTableCalculations(config, values);

  config.rows.forEach((row) => {
    if (!row.lessThanOrEqualToRow) return;

    getEditableColumns(config).forEach((column) => {
      const sourceField = getDynamicTableFieldName(row.lessThanOrEqualToRow as string, column.key);
      const dependentField = getDynamicTableFieldName(row.key, column.key);

      if (isExplicitZero(nextValues[sourceField])) {
        nextValues[dependentField] = '0';
      }
    });
  });

  return applyDynamicTableCalculations(config, nextValues);
};

export const validateDynamicTableConditions = (
  config: DynamicTableConfig,
  values: Record<string, string>,
): true | string => {
  const getRowErrorReference = (row: DynamicTableRowConfig) =>
    row.label.match(/^#\d+/)?.[0] ?? row.label;

  for (const row of config.rows) {
    if (!row.lessThanOrEqualToRow) continue;

    const sourceRow = config.rows.find((currentRow) => currentRow.key === row.lessThanOrEqualToRow);
    if (!sourceRow) continue;

    for (const column of getEditableColumns(config)) {
      const sourceField = getDynamicTableFieldName(sourceRow.key, column.key);
      const dependentField = getDynamicTableFieldName(row.key, column.key);
      if (!hasValue(values[sourceField]) || !hasValue(values[dependentField])) continue;

      if (toNumber(values[dependentField]) > toNumber(values[sourceField])) {
        return `The answer in row ${getRowErrorReference(
          row,
        )} must be less than or equal to the answer in row ${getRowErrorReference(sourceRow)}`;
      }
    }
  }

  return true;
};

const parseInitialValues = (config: DynamicTableConfig, defaultValue?: string) =>
  applyDynamicTableConditions(config, stringToObject(defaultValue));

const hasAnyEditableValue = (config: DynamicTableConfig, values: Record<string, string>) =>
  getDynamicTableEditableKeys(config).some(
    (key) => values[key] !== undefined && values[key] !== '',
  );

const containsTargetValue = (value: unknown, targetValue: string) =>
  String(value ?? '').includes(targetValue);

const getScsHeaderLines = (label: string) => {
  const match = /^SCS\s+([1-3])$/.exec(label.trim());
  return match ? ['SCS', match[1]] : undefined;
};

const renderColumnLabel = (label: string) => {
  const headerLines = getScsHeaderLines(label);
  if (!headerLines) return label;

  return (
    <>
      <span className="govuk-visually-hidden">{label}</span>
      <span aria-hidden="true">
        <span data-testid="dynamic-table-header-line">{headerLines[0]}</span>
        <br />
        <span data-testid="dynamic-table-header-line">{headerLines[1]}</span>
      </span>
    </>
  );
};

export function DynamicTable({
  question,
  disabled = false,
  anchorId = useId(),
  detailsHeading,
  detailsContent,
  interpolationValues: externalInterpolationValues,
}: Props) {
  const { register, getFieldState, getValues, setValue, control } = useFormContext();
  const { error } = getFieldState(question.name);
  const config = question.dynamicTable;
  const editableKeys = useMemo(() => getDynamicTableEditableKeys(config), [config]);
  const allKeys = useMemo(() => getDynamicTableKeys(config), [config]);
  const watchedFormValues = useWatch({ control }) as Record<string, unknown> | undefined;
  const interpolationValues = {
    ...(externalInterpolationValues || {}),
    ...(getValues() as Record<string, unknown>),
    ...(watchedFormValues || {}),
  };

  const initialValues = useMemo(
    () => (config ? parseInitialValues(config, getValues(question.name)) : {}),
    [config, getValues, question.name],
  );
  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    if (getValues(question.name) === undefined) {
      setValue(question.name, '');
    }
  }, [getValues, question.name, setValue]);

  const { onChange, name } = register(question.name, {
    validate: (value: string) => {
      if (!config) return true;
      const parsedValue = applyDynamicTableConditions(config, stringToObject(value));
      return chainValidations([
        question.rules?.required
          ? checkAllFieldsHaveValues({
              value: parsedValue,
              keys: editableKeys,
            })
          : true,
        validateDynamicTableConditions(config, parsedValue),
        groupValidation({
          value: parsedValue,
          question,
          sumColumns: allKeys,
        }),
      ]);
    },
  });

  const triggerChange = (updatedValues: Record<string, string>) => {
    if (!config) return;
    const value = hasAnyEditableValue(config, updatedValues) ? objectToString(updatedValues) : '';
    onChange({ target: { name, value } });
    applyDynamicTableTargetEffects(updatedValues);
  };

  const applyDynamicTableTargetEffects = (updatedValues: Record<string, string>) => {
    if (!config) return;

    config.rows.forEach((row) => {
      const targetConfig = row.setQuestionWhenRowTotalEquals;
      if (!targetConfig || !isRowFullyAnswered(config, row.key, updatedValues)) return;

      const currentTargetValue = getValues(targetConfig.targetQuestion);
      const matchesTargetValue = getRowTotal(config, row.key, updatedValues) === targetConfig.value;
      if (matchesTargetValue && String(currentTargetValue ?? '') !== targetConfig.targetValue) {
        setValue(targetConfig.targetQuestion, targetConfig.targetValue, {
          shouldDirty: false,
          shouldValidate: true,
        });
        return;
      }
      if (!matchesTargetValue && containsTargetValue(currentTargetValue, targetConfig.targetValue)) {
        setValue(targetConfig.targetQuestion, '', {
          shouldDirty: false,
          shouldValidate: true,
        });
      }
    });
  };

  const handleValueChange =
    (row: DynamicTableRowConfig, field: string) => (event: ChangeEvent<HTMLInputElement>) => {
      if (!config) return;
      const updatedValues = applyDynamicTableConditions(config, {
        ...values,
        [field]: normaliseDynamicTableInputValue(event.target.value, row),
      });
      setValues(updatedValues);
      triggerChange(updatedValues);
    };

  const generateInputId = (field: string) => `${anchorId}-${field}`;

  if (!config) return null;

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}
      <table className="govuk-table">
        <caption className="govuk-visually-hidden">{question.title}</caption>
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              {config.rowHeader ?? 'Question'}
            </th>
            {config.columns.map((column) => (
              <th key={column.key} scope="col" className="govuk-table__header">
                {renderColumnLabel(column.label)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {config.rows.map((row) => {
            const rowHint = interpolateQuestionText(row.hint, interpolationValues);
            return (
              <tr key={row.key} className="govuk-table__row">
                <th scope="row" className="govuk-table__header">
                  {row.label}
                  {rowHint && (
                    <div className="govuk-hint govuk-!-margin-top-1 govuk-!-margin-bottom-0">
                      {rowHint}
                    </div>
                  )}
                </th>
                {config.columns.map((column) => {
                  const field = getDynamicTableFieldName(row.key, column.key);
                  const inputDisabled = disabled || !isEditableColumn(column);
                  return (
                    <td key={field} className="govuk-table__cell">
                      <div className="govuk-form-group">
                        <label
                          className="govuk-label govuk-visually-hidden"
                          htmlFor={generateInputId(field)}
                        >
                          {`${row.label} ${column.label}`}
                        </label>
                        <input
                          className="govuk-input govuk-input--width-5"
                          id={generateInputId(field)}
                          type="number"
                          step={row.allowDecimal ? 'any' : '1'}
                          min={row.allowNegativeNumbers ? undefined : '0'}
                          inputMode={row.allowDecimal ? 'decimal' : 'numeric'}
                          value={values[field] ?? ''}
                          disabled={inputDisabled}
                          onChange={handleValueChange(row, field)}
                          aria-label={`${row.label} ${column.label}`}
                          style={{ backgroundColor: inputDisabled ? '#b1b4b6' : 'transparent' }}
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

export default DynamicTable;
